import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
    Activity,
    ArrowLeft,
    ArrowUpRight,
    Compass,
    Globe2,
    ListFilter,
    Loader2,
    LockKeyhole,
    MessageCircle,
    Search,
    Sparkles,
    UserPlus,
    UserRound,
    Users,
    UsersRound,
    X,
} from "lucide-react";
import FooterNav from "../components/FooterNav";
import PostItem from "../components/PostItem";
import {
    emptySections,
    emptyTotals,
    fetchDiscover,
    trackDiscoverEvent,
    type DiscoverItem,
    type DiscoverSections,
    type DiscoverSort,
    type DiscoverType,
} from "../api/discover";

const DISCOVER_LIMIT = 18;

const typeFilters: Array<{ id: DiscoverType; label: string; icon: typeof Search }> = [
    { id: "all", label: "All", icon: Compass },
    { id: "people", label: "People", icon: UserRound },
    { id: "spaces", label: "Spaces", icon: UsersRound },
    { id: "posts", label: "Posts", icon: MessageCircle },
];

const sortModes: Array<{ id: DiscoverSort; label: string }> = [
    { id: "for-you", label: "For you" },
    { id: "popular", label: "Moving" },
    { id: "newest", label: "Fresh" },
];

const typeToKind: Record<Exclude<DiscoverType, "all">, DiscoverItem["kind"]> = {
    people: "person",
    spaces: "space",
    posts: "post",
};

function getCurrentUsername(): string {
    try {
        const raw = localStorage.getItem("user");
        if (raw) return JSON.parse(raw)?.username || "";
    } catch {
        return "";
    }
    return "";
}

function initialFor(name: string) {
    return (name || "?").slice(0, 1).toUpperCase();
}

function SectionHeader({
    title,
    count,
    onOpen,
    actionLabel = "See all",
    icon: Icon,
}: {
    title: string;
    count: number;
    onOpen?: () => void;
    actionLabel?: string;
    icon?: typeof Search;
}) {
    return (
        <div className="bs-discover-section-head">
            <div className="bs-discover-section-title">
                {Icon && (
                    <span className="bs-discover-section-icon">
                        <Icon size={14} />
                    </span>
                )}
                <div>
                    <h2>{title}</h2>
                    <p>{count} signal{count === 1 ? "" : "s"}</p>
                </div>
            </div>
            {onOpen && count > 0 && (
                <button
                    type="button"
                    onClick={onOpen}
                    className="bs-discover-see-all"
                >
                    {actionLabel} <ArrowUpRight size={13} />
                </button>
            )}
        </div>
    );
}

function PersonCard({ item, onClick }: { item: DiscoverItem; onClick?: () => void }) {
    const name = item.displayName || item.title || item.username || "User";
    return (
        <Link to={item.href || `/user/${item.username}`} onClick={onClick} className="bs-discover-card bs-discover-person-card group">
            <div className="bs-discover-person-layout">
                <div className="bs-discover-avatar-wrap">
                    {item.avatarUrl ? (
                        <img src={item.avatarUrl} alt="" className="bs-discover-person-avatar" />
                    ) : (
                        <span className="bs-discover-person-avatar bs-discover-person-initial">
                            {initialFor(name)}
                        </span>
                    )}
                    <span className="bs-discover-presence-dot" aria-hidden="true" />
                </div>
                <div className="bs-discover-person-body">
                    <div className="bs-discover-person-name">
                        <p>{name}</p>
                        <ArrowUpRight size={13} />
                    </div>
                    <p className="bs-discover-username">{item.subtitle || (item.username ? `@${item.username}` : "")}</p>
                    {item.content && <p className="bs-discover-person-content">{item.content}</p>}
                    {item.because && (
                        <p className="bs-discover-because">
                            <Activity size={12} />
                            {item.because}
                        </p>
                    )}
                </div>
                <span className="bs-discover-follow-action" aria-hidden="true">
                    <UserPlus size={17} />
                </span>
            </div>
        </Link>
    );
}

function SpaceCard({ item, onOpen }: { item: DiscoverItem; onOpen: (id: string) => void }) {
    const accent = item.accentColor || "#22d3ee";
    const isOpen = (item.accessMode || "open") === "open";
    return (
        <button
            type="button"
            onClick={() => onOpen(item.id)}
            className="bs-discover-card bs-discover-space-card group text-left"
            style={{ "--discover-accent": accent } as CSSProperties}
        >
            <div className="bs-discover-space-layout">
                <div className="bs-discover-space-icon">
                    {isOpen ? <Globe2 size={21} /> : <LockKeyhole size={20} />}
                </div>
                <div className="bs-discover-space-body">
                    <div className="bs-discover-space-head">
                        <div>
                            <h3>{item.title}</h3>
                            <p>{item.subtitle || item.category || "Space"}</p>
                        </div>
                        <span className="bs-discover-open-action" aria-hidden="true">
                            <ArrowUpRight size={15} />
                        </span>
                    </div>
                    <p className="bs-discover-space-desc">{item.content || "A Blackspace community."}</p>
                </div>
            </div>
            <div className="bs-discover-space-stats">
                <span><Users size={12} /> {item.membersCount || item.members || 0} members</span>
                {Boolean(item.recentPosts) && <span><MessageCircle size={12} /> {item.recentPosts} recent posts</span>}
                {item.isJoined && <span className="text-cyan-300">Joined</span>}
            </div>
            {item.because && (
                <p className="bs-discover-because">
                    <Sparkles size={12} />
                    {item.because}
                </p>
            )}
        </button>
    );
}

function PostCard({
    item,
    currentUsername,
    onSelfClick,
    onOpen,
}: {
    item: DiscoverItem;
    currentUsername: string;
    onSelfClick: () => void;
    onOpen?: () => void;
}) {
    if (item.post) {
        return (
            <article className="bs-discover-post-card">
                <div className="mb-3 flex items-center justify-between gap-3 text-[11px] font-black text-cyan-300/80">
                    <span>{item.because || "because it is moving now"}</span>
                    <Link to={item.href || `/post/${item.id}`} onClick={onOpen} className="text-neutral-500 transition hover:text-cyan-300" title="Open post">
                        <ArrowUpRight size={14} />
                    </Link>
                </div>
                <PostItem post={item.post} currentUsername={currentUsername} onSelfClick={onSelfClick} />
            </article>
        );
    }

    return (
        <Link to={item.href || `/post/${item.id}`} onClick={onOpen} className="bs-discover-card group">
            <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                    <p className="truncate text-sm font-black text-white">{item.title}</p>
                    <p className="truncate text-xs text-neutral-500">{item.subtitle}</p>
                </div>
                <ArrowUpRight size={14} className="text-neutral-600 transition group-hover:text-cyan-300" />
            </div>
            <p className="mt-3 line-clamp-3 text-sm text-neutral-300">{item.content}</p>
            <p className="mt-3 text-[11px] font-bold text-cyan-300/80">{item.because}</p>
        </Link>
    );
}

function EmptyState({ label }: { label: string }) {
    return (
        <div className="bs-discover-empty">
            <Sparkles size={18} />
            <span>
                <strong>{label}</strong>
                <small>Check back later for signals from your world.</small>
            </span>
        </div>
    );
}

export default function Discover() {
    const navigate = useNavigate();
    const location = useLocation();
    const routeState = location.state as { from?: string; reopenBlackspaceMenuOnBack?: boolean } | null;
    const originRef = useRef<string | null>(routeState?.from || null);
    const reopenMenuOnBackRef = useRef(Boolean(routeState?.reopenBlackspaceMenuOnBack));
    const [params, setParams] = useSearchParams();
    const currentUsername = getCurrentUsername();
    const [query, setQuery] = useState(params.get("q") || "");
    const [submittedQuery, setSubmittedQuery] = useState(params.get("q") || "");
    const [activeType, setActiveType] = useState<DiscoverType>((params.get("type") as DiscoverType) || "all");
    const [sortMode, setSortMode] = useState<DiscoverSort>((params.get("sort") as DiscoverSort) || "for-you");
    const [searchOpen, setSearchOpen] = useState(Boolean(params.get("q")));
    const [filterOpen, setFilterOpen] = useState(false);
    const [items, setItems] = useState<DiscoverItem[]>([]);
    const [sections, setSections] = useState<DiscoverSections>(emptySections);
    const [totals, setTotals] = useState(emptyTotals);
    const [hasMore, setHasMore] = useState(false);
    const [nextCursor, setNextCursor] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const abortRef = useRef<AbortController | null>(null);
    const searchRef = useRef<HTMLInputElement | null>(null);
    const loadMoreRef = useRef<HTMLDivElement | null>(null);
    const trackedImpressionsRef = useRef<Set<string>>(new Set());

    const filterIsActive = activeType !== "all" || sortMode !== "for-you";

    const runDiscover = useCallback(async (cursor: string | null = null, signal?: AbortSignal, typeOverride?: DiscoverType, replaceUrl = true) => {
        const clean = query.trim();
        const requestedType = typeOverride || activeType;
        setSubmittedQuery(clean);
        const nextParams: Record<string, string> = {};
        if (clean) nextParams.q = clean;
        if (requestedType !== "all") nextParams.type = requestedType;
        if (sortMode !== "for-you") nextParams.sort = sortMode;
        setParams(nextParams, { replace: replaceUrl });

        if (!cursor) setLoading(true);
        else setLoadingMore(true);
        setError(null);
        try {
            const result = await fetchDiscover({
                q: clean || undefined,
                type: requestedType,
                sort: sortMode,
                cursor,
                limit: DISCOVER_LIMIT,
                signal,
            });
            setItems((current) => {
                if (!cursor) return result.items;
                const seen = new Set(current.map((item) => `${item.kind}:${item.id}`));
                return [...current, ...result.items.filter((item) => !seen.has(`${item.kind}:${item.id}`))];
            });
            setSections(result.sections || emptySections);
            setTotals(result.totals || emptyTotals);
            setHasMore(Boolean(result.hasMore));
            setNextCursor(result.nextCursor);
        } catch (err: any) {
            if (err?.name === "AbortError") return;
            setError(err?.message || "Discover failed.");
            if (!cursor) {
                setItems([]);
                setSections(emptySections);
                setTotals(emptyTotals);
                setHasMore(false);
                setNextCursor(null);
            }
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [activeType, query, setParams, sortMode]);

    useEffect(() => {
        const timer = window.setTimeout(() => {
            abortRef.current?.abort();
            const controller = new AbortController();
            abortRef.current = controller;
            runDiscover(null, controller.signal);
        }, query.trim() ? 280 : 120);
        return () => {
            window.clearTimeout(timer);
            abortRef.current?.abort();
        };
    }, [query, runDiscover]);

    useEffect(() => {
        if (!searchOpen) return;
        const timer = window.setTimeout(() => searchRef.current?.focus(), 60);
        return () => window.clearTimeout(timer);
    }, [searchOpen]);

    const loadMore = useCallback(() => {
        if (activeType === "all" || loading || loadingMore || !hasMore || !nextCursor) return;
        const controller = new AbortController();
        runDiscover(nextCursor, controller.signal);
    }, [activeType, hasMore, loading, loadingMore, nextCursor, runDiscover]);

    useEffect(() => {
        const node = loadMoreRef.current;
        if (!node) return;
        const observer = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) loadMore();
        }, { rootMargin: "260px 0px" });
        observer.observe(node);
        return () => observer.disconnect();
    }, [loadMore]);

    const activeItems = useMemo(() => {
        if (activeType === "all") return items;
        return items.filter((item) => item.kind === typeToKind[activeType]);
    }, [activeType, items]);

    const hasResults = activeType === "all"
        ? sections.people.length > 0 || sections.spaces.length > 0 || sections.posts.length > 0
        : activeItems.length > 0;
    const goBack = () => {
        const currentRoute = `${location.pathname}${location.search}${location.hash}`;
        if (originRef.current && originRef.current !== currentRoute) {
            navigate(originRef.current, { state: reopenMenuOnBackRef.current ? { reopenBlackspaceMenu: true } : undefined });
            return;
        }
        if (window.history.length > 1) {
            navigate(-1);
            return;
        }
        navigate("/home");
    };

    function switchType(type: DiscoverType) {
        setActiveType(type);
        setFilterOpen(false);
        setSearchOpen(false);
        if (type === "all") {
            setItems([]);
        } else {
        setItems(sections[type] || []);
            setNextCursor(null);
            setHasMore(false);
        }
        window.scrollTo({ top: 0, behavior: "smooth" });
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        runDiscover(null, controller.signal, type, type === activeType);
    }

    function trackClick(item: DiscoverItem, position: number) {
        trackDiscoverEvent({
            type: "click",
            surface: activeType,
            query: submittedQuery,
            itemKind: item.kind,
            itemId: item.id,
            position,
        });
    }

    function renderItem(item: DiscoverItem, index = 0) {
        if (item.kind === "person") return <PersonCard key={`${item.kind}:${item.id}`} item={item} onClick={() => trackClick(item, index)} />;
        if (item.kind === "space") {
            return (
                <SpaceCard
                    key={`${item.kind}:${item.id}`}
                    item={item}
                    onOpen={(id) => {
                        trackClick(item, index);
                        navigate(`/spaces/${id}`);
                    }}
                />
            );
        }
        return <PostCard key={`${item.kind}:${item.id}`} item={item} currentUsername={currentUsername} onSelfClick={() => navigate("/home")} onOpen={() => trackClick(item, index)} />;
    }

    useEffect(() => {
        const visible = activeType === "all"
            ? [...sections.people, ...sections.spaces, ...sections.posts]
            : activeItems;
        const fresh = visible
            .map((item, index) => ({ kind: item.kind, id: item.id, position: index }))
            .filter((item) => {
                const key = `${activeType}:${submittedQuery}:${item.kind}:${item.id}`;
                if (trackedImpressionsRef.current.has(key)) return false;
                trackedImpressionsRef.current.add(key);
                return true;
            })
            .slice(0, 24);
        if (!fresh.length) return;
        trackDiscoverEvent({
            type: "impression",
            surface: activeType,
            query: submittedQuery,
            items: fresh,
        });
    }, [activeItems, activeType, sections.people, sections.posts, sections.spaces, submittedQuery]);

    return (
        <div className="bs-page bs-discover-page flex min-h-screen flex-col">
            <main className="bs-discover-main mx-auto w-full max-w-6xl flex-1 px-4 py-5 pb-28 sm:px-8">
                <header className="bs-discover-hero">
                    <div className="bs-discover-orbit" aria-hidden="true" />
                    <div className="bs-global-search-topbar">
                        <button
                            type="button"
                            onClick={goBack}
                            className="bs-global-search-back"
                            title="Back"
                        >
                            <ArrowLeft size={14} />
                        </button>
                        <span className="bs-global-search-live bs-message-page-title">
                            <Sparkles size={13} />
                            Discover
                        </span>
                        <div className="bs-global-search-actions">
                            <button
                                type="button"
                                className={`bs-home-mobile-command ${searchOpen || query.trim() ? "is-active" : ""}`}
                                onClick={() => {
                                    setSearchOpen((open) => !open);
                                    setFilterOpen(false);
                                }}
                                title="Search"
                                aria-label="Search"
                                aria-pressed={searchOpen}
                            >
                                <Search size={18} />
                            </button>
                            <button
                                type="button"
                                className={`bs-home-mobile-command ${filterOpen || filterIsActive ? "is-active" : ""}`}
                                onClick={() => {
                                    setFilterOpen((open) => !open);
                                    setSearchOpen(false);
                                }}
                                title="Filters"
                                aria-label="Filters"
                                aria-pressed={filterOpen}
                            >
                                <ListFilter size={18} />
                            </button>
                        </div>
                    </div>

                    {searchOpen && (
                        <div className="bs-home-mobile-sheet bs-global-search-feed-sheet">
                            <form
                                className="bs-home-mobile-search-form flex items-center gap-2"
                                onSubmit={(event) => {
                                    event.preventDefault();
                                    searchRef.current?.blur();
                                }}
                            >
                                <Search size={17} className="shrink-0 text-cyan-200" />
                                <input
                                    ref={searchRef}
                                    type="search"
                                    value={query}
                                    onChange={(event) => setQuery(event.target.value)}
                                    placeholder="Search people, spaces, posts..."
                                    autoComplete="off"
                                    autoCorrect="off"
                                    spellCheck={false}
                                    enterKeyHint="search"
                                    inputMode="search"
                                    className="bs-mobile-search-input min-w-0 flex-1 bg-transparent text-sm font-semibold outline-none"
                                />
                                {query && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setQuery("");
                                            setSubmittedQuery("");
                                            searchRef.current?.focus();
                                        }}
                                        className="grid h-8 w-8 shrink-0 place-items-center rounded-md border border-neutral-800 bg-black/45 text-neutral-400"
                                        aria-label="Clear search"
                                    >
                                        <X size={15} />
                                    </button>
                                )}
                            </form>
                        </div>
                    )}
                </header>

                {filterOpen && (
                    <section className="bs-home-mobile-sheet bs-global-search-feed-sheet bs-global-search-feed-filter" aria-label="Discover filters">
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                            {typeFilters.map(({ id, label, icon: Icon }) => (
                                <button
                                    key={id}
                                    type="button"
                                    onClick={() => switchType(id)}
                                    className={`bs-home-mobile-filter-choice ${activeType === id ? "is-active" : ""}`}
                                >
                                    <Icon size={14} />
                                    {label}
                                </button>
                            ))}
                        </div>
                        <div className="mt-2 grid grid-cols-3 gap-2">
                            {sortModes.map((mode) => (
                                <button
                                    key={mode.id}
                                    type="button"
                                    onClick={() => setSortMode(mode.id)}
                                    className={`bs-home-mobile-filter-choice ${sortMode === mode.id ? "is-active" : ""}`}
                                >
                                    <Sparkles size={14} />
                                    {mode.label}
                                </button>
                            ))}
                        </div>
                    </section>
                )}

                {submittedQuery && (
                <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-neutral-500">
                    {submittedQuery && <span>Showing results for "{submittedQuery}"</span>}
                </div>
                )}

                {error && <div className="mt-4 rounded-3xl border border-rose-500/30 bg-rose-950/20 p-4 text-sm font-bold text-rose-200">{error}</div>}

                <section className="bs-discover-content mt-5 space-y-6">
                    {activeType === "all" ? (
                        <>
                            <section>
                                <SectionHeader title="People rising" count={totals.people} onOpen={() => switchType("people")} icon={Activity} />
                                {sections.people.length ? (
                                    <div className="bs-discover-list">
                                        {sections.people.map(renderItem)}
                                    </div>
                                ) : !loading && <EmptyState label="No people surfaced yet." />}
                            </section>

                            <section>
                                <SectionHeader title="Spaces gaining life" count={totals.spaces} onOpen={() => switchType("spaces")} />
                                {sections.spaces.length ? (
                                    <div className="bs-discover-list">
                                        {sections.spaces.map(renderItem)}
                                    </div>
                                ) : !loading && <EmptyState label="No spaces surfaced yet." />}
                            </section>

                            <section>
                                <SectionHeader title="Posts being felt" count={totals.posts} onOpen={() => switchType("posts")} />
                                {sections.posts.length ? (
                                    <div className="space-y-3">
                                        {sections.posts.map(renderItem)}
                                    </div>
                                ) : !loading && <EmptyState label="No posts surfaced yet." />}
                            </section>
                        </>
                    ) : (
                        <section>
                            <SectionHeader
                                title={activeType === "people" ? "People" : activeType === "spaces" ? "Spaces" : "Posts"}
                                count={totals[activeType]}
                                onOpen={() => switchType("all")}
                                actionLabel="Back"
                            />
                            {loading && (
                                <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-cyan-300/10 px-3 py-1.5 text-xs font-black text-cyan-200">
                                    <Loader2 size={13} className="animate-spin" />
                                    Loading {activeType}
                                </div>
                            )}
                            {activeItems.length ? (
                                <div className={activeType === "posts" ? "space-y-3" : "bs-discover-list"}>
                                    {activeItems.map(renderItem)}
                                </div>
                            ) : !loading && <EmptyState label="Nothing matched here yet." />}
                            <div ref={loadMoreRef} className="h-8" />
                            {loadingMore && (
                                <div className="flex justify-center py-4 text-sm font-bold text-cyan-300">
                                    <Loader2 size={17} className="mr-2 animate-spin" />
                                    Loading more
                                </div>
                            )}
                        </section>
                    )}

                    {!loading && !hasResults && !error && (
                        <EmptyState label="Discover is quiet for this search. Try a broader term." />
                    )}
                </section>
            </main>
            <FooterNav />
        </div>
    );
}
